import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { switchMfaFactor } from '../actions/multi-factor-actions';
import ButtonLink from '../components/ButtonLink';
import messages from './MultiFactorPage.messages';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { MessageDescriptor, MultifactorMethod } from '../types';
import {
  useAnalyticsClient,
  useButtonClickedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { Box } from '@atlaskit/primitives';

export type MultiFactorSwitchMethodsProps = {
  screenId: string;
  isSwitching?: boolean;
  switchWithTracking: <T>(asyncOperation: () => Promise<T>) => Promise<T>;
};

type SwitchableFactorButton = {
  // the factor to switch to when the button is clicked
  method: MultifactorMethod;
  // button label for the factor
  buttonLabel: MessageDescriptor;
  // click handler for the button
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
};
export const MultiFactorSwitchMethods = ({
  screenId,
  isSwitching,
  switchWithTracking,
}: MultiFactorSwitchMethodsProps) => {
  const dispatch = useAppDispatch();

  const { method: multifactorMethod, additionalMfaFactors } = useAppSelector(
    state => state.multiFactor
  );

  const { search } = useLocation();
  const navigate = useNavigateSoftOrHard();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  const trackSwitchToSms = useButtonClickedEvent(screenId, 'switchToSms');
  const trackSwitchToTotp = useButtonClickedEvent(screenId, 'switchToTotp');
  const trackSwitchToSecurityKey = useButtonClickedEvent(screenId, 'switchToSecurityKey');

  const onSwitchToSms = useCallback(() => {
    trackSwitchToSms();
    switchWithTracking(
      async () => await dispatch(switchMfaFactor(search, 'SMS', analyticsClient, navigate))
    );
  }, [trackSwitchToSms, switchWithTracking, dispatch, search, analyticsClient, navigate]);

  const onSwitchToTotp = useCallback(() => {
    trackSwitchToTotp();
    switchWithTracking(
      async () => await dispatch(switchMfaFactor(search, 'TOTP', analyticsClient, navigate))
    );
  }, [trackSwitchToTotp, switchWithTracking, dispatch, search, analyticsClient, navigate]);

  const onSwitchToSecurityKey = useCallback(() => {
    trackSwitchToSecurityKey();
    switchWithTracking(
      async () => await dispatch(switchMfaFactor(search, 'SecurityKey', analyticsClient, navigate))
    );
  }, [trackSwitchToSecurityKey, switchWithTracking, dispatch, search, analyticsClient, navigate]);

  const switchableFactors: SwitchableFactorButton[] = useMemo(
    () => [
      {
        method: 'security-key',
        buttonLabel: messages.mfSwitchToSecurityKey,
        onClick: onSwitchToSecurityKey,
      },
      {
        method: 'otp',
        buttonLabel: messages.mfSwitchToTotp,
        onClick: onSwitchToTotp,
      },
      {
        method: 'sms',
        buttonLabel: messages.mfSwitchToSms,
        onClick: onSwitchToSms,
      },
    ],
    [onSwitchToSecurityKey, onSwitchToTotp, onSwitchToSms]
  );

  if (!additionalMfaFactors || additionalMfaFactors.length === 0) {
    return null;
  }
  return (
    <>
      <Box paddingBlockEnd="space.150">
        <FormattedMessage
          tagName="p"
          id="mf.switch-to-other-factor-help-text"
          defaultMessage="Use alternate method"
          description="Text indicating the purposes the buttons to switch factor when being challenged on login"
        />
      </Box>
      {switchableFactors.flatMap(factor => {
        if (!additionalMfaFactors.includes(factor.method) || factor.method === multifactorMethod) {
          return [];
        } else {
          return [
            <div key={`switch-factor-${factor.method}-wrapper`}>
              <ButtonLink
                id={`two-step-verification-switch-factor-${factor.method}`}
                testId={`two-step-verification-switch-factor-${factor.method}`}
                onClick={factor.onClick}
                isDisabled={isSwitching}>
                {formatMessage(factor.buttonLabel)}
              </ButtonLink>
            </div>,
          ];
        }
      })}
    </>
  );
};

export default MultiFactorSwitchMethods;
